import { useEffect, useState, type FormEvent } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { ArrowLeft, ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

interface QuestionOption {
  label: string;
  value: string;
  score: number;
}

interface BankDetail {
  id: string;
  name: string;
  description: string | null;
  qualifyThreshold: string;
  disqualifyThreshold: string;
  maxQuestions: number | null;
  graderSystemPrompt: string | null;
  selectorStrategy: 'ROUND_ROBIN' | 'GREEDY_INFO_GAIN';
  status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  questionCount: number;
}

interface Question {
  id: string;
  bankId: string;
  profileKey: string;
  promptText: string;
  questionType: 'OPEN' | 'SINGLE_CHOICE' | 'MULTI_CHOICE' | 'NUMERIC' | 'BOOLEAN';
  options: QuestionOption[];
  gradingRubric: string | null;
  weight: string;
  sensitiveClass: boolean;
  orderIdx: number;
  required: boolean;
  status: string;
}

export function QualificationBankEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [bank, setBank] = useState<BankDetail | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  // Editable bank fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [qualifyThreshold, setQualifyThreshold] = useState('0.70');
  const [disqualifyThreshold, setDisqualifyThreshold] = useState('0.25');
  const [maxQuestions, setMaxQuestions] = useState('');
  const [selectorStrategy, setSelectorStrategy] =
    useState<'ROUND_ROBIN' | 'GREEDY_INFO_GAIN'>('ROUND_ROBIN');
  const [graderSystemPrompt, setGraderSystemPrompt] = useState('');
  const [status, setStatus] = useState<'ACTIVE' | 'PAUSED' | 'ARCHIVED'>('ACTIVE');
  const [savingBank, setSavingBank] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiClient.get<BankDetail>(`/qualification/banks/${id}`),
      apiClient.get<Question[]>(`/qualification/banks/${id}/questions`),
    ])
      .then(([b, q]) => {
        if (cancelled) return;
        setBank(b.data);
        setQuestions(q.data);
        setName(b.data.name);
        setDescription(b.data.description ?? '');
        setQualifyThreshold(b.data.qualifyThreshold);
        setDisqualifyThreshold(b.data.disqualifyThreshold);
        setMaxQuestions(b.data.maxQuestions?.toString() ?? '');
        setSelectorStrategy(b.data.selectorStrategy);
        setGraderSystemPrompt(b.data.graderSystemPrompt ?? '');
        setStatus(b.data.status);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function saveBank(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSavingBank(true);
    try {
      const { data } = await apiClient.patch<BankDetail>(`/qualification/banks/${id}`, {
        name,
        description: description.trim() || null,
        qualifyThreshold: parseFloat(qualifyThreshold),
        disqualifyThreshold: parseFloat(disqualifyThreshold),
        maxQuestions: maxQuestions ? parseInt(maxQuestions, 10) : null,
        selectorStrategy,
        graderSystemPrompt: graderSystemPrompt.trim() || null,
        status,
      });
      setBank(data);
      setToast('Saved');
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setToast(detail ?? 'Failed to save');
    } finally {
      setSavingBank(false);
      setTimeout(() => setToast(null), 2500);
    }
  }

  async function archive() {
    if (!id) return;
    if (!confirm('Archive this bank? Existing conversations are unaffected.')) return;
    try {
      await apiClient.delete(`/qualification/banks/${id}`);
      navigate('/admin/qualification-banks');
    } catch {
      setToast('Failed to archive');
      setTimeout(() => setToast(null), 2500);
    }
  }

  async function reloadQuestions() {
    if (!id) return;
    const { data } = await apiClient.get<Question[]>(`/qualification/banks/${id}/questions`);
    setQuestions(data);
  }

  async function addQuestion() {
    if (!id) return;
    try {
      await apiClient.post<Question>(`/qualification/banks/${id}/questions`, {
        profileKey: `q${questions.length + 1}`,
        promptText: 'New question',
        questionType: 'OPEN',
        options: [],
        gradingRubric: null,
        weight: 1.0,
        sensitiveClass: false,
        orderIdx: (questions.at(-1)?.orderIdx ?? 0) + 10,
        required: true,
      });
      await reloadQuestions();
    } catch {
      setToast('Failed to add question');
      setTimeout(() => setToast(null), 2500);
    }
  }

  async function patchQuestion(q: Question, patch: Partial<Question>) {
    if (!id) return;
    try {
      await apiClient.patch(`/qualification/banks/${id}/questions/${q.id}`, patch);
      await reloadQuestions();
    } catch {
      setToast('Failed to update question');
      setTimeout(() => setToast(null), 2500);
    }
  }

  async function deleteQuestion(q: Question) {
    if (!id) return;
    if (!confirm(`Delete question "${q.profileKey}"?`)) return;
    try {
      await apiClient.delete(`/qualification/banks/${id}/questions/${q.id}`);
      await reloadQuestions();
    } catch {
      setToast('Failed to delete');
      setTimeout(() => setToast(null), 2500);
    }
  }

  async function moveQuestion(idx: number, dir: -1 | 1) {
    const target = idx + dir;
    if (target < 0 || target >= questions.length) return;
    const a = questions[idx];
    const b = questions[target];
    await Promise.all([
      patchQuestion(a, { orderIdx: b.orderIdx }),
      patchQuestion(b, { orderIdx: a.orderIdx }),
    ]);
  }

  if (loading || !bank) {
    return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <Link
        to="/admin/qualification-banks"
        className="inline-flex items-center gap-1 text-sm text-primary"
      >
        <ArrowLeft className="h-4 w-4" /> Back to banks
      </Link>

      <Card>
        <h1 className="text-2xl font-semibold">{bank.name}</h1>
        <p className="mt-1 text-xs text-muted-foreground">
          {bank.questionCount} active question{bank.questionCount === 1 ? '' : 's'} ·
          status {bank.status}
        </p>
      </Card>

      <form onSubmit={saveBank} className="space-y-4">
        <Card>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Bank settings
          </h2>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Description</label>
              <textarea
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-md border bg-background p-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">Qualify threshold</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={qualifyThreshold}
                  onChange={(e) => setQualifyThreshold(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Disqualify threshold</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={disqualifyThreshold}
                  onChange={(e) => setDisqualifyThreshold(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Max questions</label>
                <Input
                  type="number"
                  min="1"
                  value={maxQuestions}
                  onChange={(e) => setMaxQuestions(e.target.value)}
                  placeholder="No cap"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">Selector strategy</label>
                <select
                  value={selectorStrategy}
                  onChange={(e) =>
                    setSelectorStrategy(
                      e.target.value as 'ROUND_ROBIN' | 'GREEDY_INFO_GAIN',
                    )
                  }
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="ROUND_ROBIN">Round robin</option>
                  <option value="GREEDY_INFO_GAIN">Greedy info gain</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Status</label>
                <select
                  value={status}
                  onChange={(e) =>
                    setStatus(e.target.value as 'ACTIVE' | 'PAUSED' | 'ARCHIVED')
                  }
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="ACTIVE">Active</option>
                  <option value="PAUSED">Paused</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Grader system prompt (LLM)</label>
              <textarea
                rows={3}
                value={graderSystemPrompt}
                onChange={(e) => setGraderSystemPrompt(e.target.value)}
                className="w-full rounded-md border bg-background p-2 text-sm"
                placeholder="Optional system prompt for LLM-graded OPEN questions."
              />
            </div>
          </div>
        </Card>

        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={archive}
            className="text-destructive hover:bg-destructive/10"
          >
            Archive bank
          </Button>
          <div className="flex items-center gap-3">
            {toast && <span className="text-sm text-muted-foreground">{toast}</span>}
            <Button type="submit" disabled={savingBank}>
              {savingBank ? 'Saving…' : 'Save bank settings'}
            </Button>
          </div>
        </div>
      </form>

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Questions
          </h2>
          <Button size="sm" variant="outline" onClick={addQuestion}>
            <Plus className="mr-1 h-3 w-3" /> Add question
          </Button>
        </div>
        {questions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No questions. Add at least one before activating the bank.
          </p>
        )}
        <div className="space-y-3">
          {questions.map((q, idx) => (
            <QuestionRow
              key={q.id}
              q={q}
              first={idx === 0}
              last={idx === questions.length - 1}
              onMove={(dir) => moveQuestion(idx, dir)}
              onPatch={(patch) => patchQuestion(q, patch)}
              onDelete={() => deleteQuestion(q)}
            />
          ))}
        </div>
      </Card>
    </div>
  );
}

function QuestionRow({
  q,
  first,
  last,
  onMove,
  onPatch,
  onDelete,
}: {
  q: Question;
  first: boolean;
  last: boolean;
  onMove: (dir: -1 | 1) => void;
  onPatch: (patch: Partial<Question>) => void;
  onDelete: () => void;
}) {
  const [profileKey, setProfileKey] = useState(q.profileKey);
  const [promptText, setPromptText] = useState(q.promptText);
  const [weight, setWeight] = useState(q.weight);
  const [rubric, setRubric] = useState(q.gradingRubric ?? '');
  const [optionsText, setOptionsText] = useState(
    q.options.map((o) => `${o.value}|${o.label}|${o.score}`).join('\n'),
  );

  function persist() {
    const options = parseOptions(optionsText);
    onPatch({
      profileKey,
      promptText,
      weight,
      gradingRubric: rubric.trim() || null,
      options,
    });
  }

  return (
    <div className="grid grid-cols-12 gap-3 rounded-md bg-muted/40 p-3">
      <div className="col-span-1 flex flex-col items-center gap-1">
        <button
          type="button"
          onClick={() => onMove(-1)}
          disabled={first}
          className="rounded p-1 hover:bg-muted disabled:opacity-30"
          title="Move up"
        >
          <ArrowUp className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={() => onMove(1)}
          disabled={last}
          className="rounded p-1 hover:bg-muted disabled:opacity-30"
          title="Move down"
        >
          <ArrowDown className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded p-1 text-destructive hover:bg-destructive/10"
          title="Delete"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
      <div className="col-span-11 space-y-2">
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-3 space-y-1">
            <label className="text-[10px] font-medium uppercase text-muted-foreground">
              Profile key
            </label>
            <Input
              value={profileKey}
              onChange={(e) => setProfileKey(e.target.value)}
              onBlur={persist}
            />
          </div>
          <div className="col-span-2 space-y-1">
            <label className="text-[10px] font-medium uppercase text-muted-foreground">
              Type
            </label>
            <select
              value={q.questionType}
              onChange={(e) =>
                onPatch({ questionType: e.target.value as Question['questionType'] })
              }
              className="w-full rounded-md border bg-background px-2 py-2 text-xs"
            >
              <option>OPEN</option>
              <option>SINGLE_CHOICE</option>
              <option>MULTI_CHOICE</option>
              <option>NUMERIC</option>
              <option>BOOLEAN</option>
            </select>
          </div>
          <div className="col-span-2 space-y-1">
            <label className="text-[10px] font-medium uppercase text-muted-foreground">
              Weight
            </label>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="10"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              onBlur={persist}
            />
          </div>
          <div className="col-span-2 flex items-center gap-1">
            <input
              type="checkbox"
              checked={q.required}
              onChange={(e) => onPatch({ required: e.target.checked })}
              id={`req-${q.id}`}
            />
            <label htmlFor={`req-${q.id}`} className="text-xs">
              Required
            </label>
          </div>
          <div className="col-span-3 flex items-center gap-1">
            <input
              type="checkbox"
              checked={q.sensitiveClass}
              onChange={(e) => onPatch({ sensitiveClass: e.target.checked })}
              id={`sens-${q.id}`}
            />
            <label htmlFor={`sens-${q.id}`} className="text-xs">
              Sensitive class (requires consent)
            </label>
          </div>
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-medium uppercase text-muted-foreground">
            Prompt
          </label>
          <textarea
            rows={2}
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            onBlur={persist}
            className="w-full rounded-md border bg-background p-2 text-sm"
          />
        </div>
        {(q.questionType === 'SINGLE_CHOICE' || q.questionType === 'MULTI_CHOICE') && (
          <div className="space-y-1">
            <label className="text-[10px] font-medium uppercase text-muted-foreground">
              Options (one per line: value|label|score, score 0..1)
            </label>
            <textarea
              rows={3}
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              onBlur={persist}
              className="w-full rounded-md border bg-background p-2 font-mono text-xs"
              placeholder="hot|Hot lead|1.0&#10;cold|Cold lead|0.2"
            />
          </div>
        )}
        {q.questionType === 'OPEN' && (
          <div className="space-y-1">
            <label className="text-[10px] font-medium uppercase text-muted-foreground">
              Grading rubric (LLM)
            </label>
            <textarea
              rows={2}
              value={rubric}
              onChange={(e) => setRubric(e.target.value)}
              onBlur={persist}
              className="w-full rounded-md border bg-background p-2 text-sm"
              placeholder="Describe what counts as a good answer."
            />
          </div>
        )}
      </div>
    </div>
  );
}

function parseOptions(text: string): QuestionOption[] {
  return text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [value, label, score] = line.split('|').map((p) => p.trim());
      return {
        value: value ?? '',
        label: label ?? value ?? '',
        score: Number.isFinite(Number(score)) ? Number(score) : 0.5,
      };
    });
}
